// =============================================================
// Firebase Authentication context
// =============================================================
// Listens to onAuthStateChanged and exposes:
//   • user             — Firebase User object or null
//   • loading          — true until first auth state is known
//   • signupWithEmail  — create user + ensure profile doc
//   • loginWithEmail   — sign in with email/password
//   • loginWithGoogle  — sign in with Google popup + ensure profile doc
//   • sendPasswordReset— trigger password-reset email
//   • logout           — sign out
//
// On every successful sign-in we make sure a profile document exists at
// /users/{uid}. Security rules guarantee the user can only read/write
// their own profile.
// =============================================================

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, googleProvider, db } from "../lib/firebase";

const AuthContext = createContext(null);

// Idempotent profile bootstrap — called after sign-up or first Google login.
async function ensureUserProfile(user, extras = {}) {
  if (!user) return;
  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      uid: user.uid,
      email: user.email,
      displayName: extras.displayName || user.displayName || user.email?.split("@")[0] || "Grappler",
      photoURL: user.photoURL || null,
      belt: "White",
      celebrated: false,
      createdAt: serverTimestamp(),
    });
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  const signupWithEmail = useCallback(async (email, password, displayName) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(cred.user, { displayName });
    }
    await ensureUserProfile(cred.user, { displayName });
    return cred.user;
  }, []);

  const loginWithEmail = useCallback(async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    await ensureUserProfile(cred.user);
    return cred.user;
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const cred = await signInWithPopup(auth, googleProvider);
    await ensureUserProfile(cred.user);
    return cred.user;
  }, []);

  const sendPasswordReset = useCallback(async (email) => {
    await sendPasswordResetEmail(auth, email);
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  const value = useMemo(
    () => ({ user, loading, signupWithEmail, loginWithEmail, loginWithGoogle, sendPasswordReset, logout }),
    [user, loading, signupWithEmail, loginWithEmail, loginWithGoogle, sendPasswordReset, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
